import { Component, inject, Injector, OnDestroy, OnInit, Signal, signal } from '@angular/core';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { isKnownTab, Tab } from '@modules/common/types/tab';
import { FilesListStateService } from '@modules/files/services/files-list-state.service';
import { FoldersListStateService } from '@modules/files/services/folders-list-state.service';
import { FilesService } from '@modules/files/services/files.service';
import { FoldersService } from '@modules/files/services/folders.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { FilesAppStateService } from '@modules/files/services/state.service';
import { File } from '@modules/files/types/file';
import { hasFileLink, openFileLink } from '@modules/files/types/file-link';
import { FilesFilters } from '@modules/files/types/files-filters';
import { Folder } from '@modules/files/types/folder';
import { FoldersFilters } from '@modules/files/types/folders-filters';
import { ListState } from '@modules/files/types/list-state';
import { VirtualFolder } from '@modules/files/types/virtual-folder';
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { combineLatest, EMPTY, forkJoin, merge, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { Application } from '@modules/common/types/application';
import { StitchServiceFactory } from '@modules/common/factories/stitch-service.factory';

@Component({
  selector: 'app-files-list-container',
  templateUrl: './list-container.component.html',
  styleUrls: ['./list-container.component.less'],
  standalone: false,
  providers: [
    ...FilesListStateService.providers({
      withAdvancedSearch: true,
      stateKey: StateKey.filesListState,
    }),
    ...FoldersListStateService.providers({
      withAdvancedSearch: true,
      stateKey: StateKey.foldersListState,
    }),
  ],
})
export class FilesListContainerComponent implements OnInit, OnDestroy {
  public minimized: boolean;
  public searchQuery: string;
  public listState: ListState;
  public sidebarFilter: VirtualFolder;
  public quickFolder: Folder;
  public quickFile: File;
  public loading: boolean;
  public tabs: Tab<Exclude<ListState, 'tabs'>>[] = [
    { title: 'Folders', value: 'folders' },
    { title: 'Files', value: 'files' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'files';
  public foldersFilters: FoldersFilters;
  public filesFilters: FilesFilters;
  public debug: 'score' = null;
  public containerName: string | null = null;

  private alive = new Subject<void>();

  /**
   * Opening the main view is selection-driven, so walking the list with the arrow keys queues
   * one open per row you land on. `switchMap` drops the fetch still in flight: it saves the
   * requests, and it stops an earlier response from arriving late and painting the wrong item.
   *
   * Each fetch is a one-shot read of `getItem`, which is bound to the service's refresh stream and
   * would otherwise re-emit — and repaint the pane — on every `forceRefresh()` for as long as this
   * container lives. And a fetch that fails once its retries are spent (the service toasts and
   * rethrows) is contained inside the switchMap, or the error would end this subject's subscription
   * and every later open would go silently dead.
   */
  private readonly openFileRequest = new Subject<File>();

  private readonly filesListStateService = inject(FilesListStateService, { self: true });
  private readonly foldersListStateService = inject(FoldersListStateService, { self: true });
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly filesService = inject(FilesService);
  private readonly foldersService = inject(FoldersService);
  private readonly spaceContextStore = inject(SpaceContextStore);

  /** The personal Flagged list holds neither files nor folders: it shows one line, not the two tabs. */
  protected readonly personalFlaggedEmpty = signal(false);

  private readonly stitchServiceFactory = inject(StitchServiceFactory);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  protected readonly isAdvancedSearchApplied: Signal<boolean> = toSignal(
    this.advancedSearchService.isAdvancedSearchApplied(Application.files),
    { initialValue: false },
  );

  constructor(
    private splitViewService: SplitViewService,
    private stateService: FilesAppStateService,
    private modalService: ModalService,
    private searchService: AdvancedSearchService,
    private injector: Injector,
  ) {
    combineLatest([
      this.stateService.getVirtualFolder(),
      this.stateService.getTeam(),
      merge(
        of(0),
        this.filesService.getRefresh(),
        this.foldersService.getRefresh(),
        this.stateService.getVirtualFolderReselections(),
      ),
    ])
      .pipe(
        switchMap(([folder, team]) => {
          const personal = !this.spaceContextStore.insideTeam() || team !== this.spaceContextStore.contextId();
          if (folder !== 'flagged' || !personal) {
            return of(false);
          }
          return forkJoin([
            this.filesService.count(new FilesFilters({ short: true }).applyVirtualFolder(folder)),
            this.foldersService.count(new FoldersFilters().applyVirtualFolder(folder)),
          ]).pipe(
            map(([files, folders]) => files + folders === 0),
            catchError(() => of(false)),
          );
        }),
        takeUntil(this.alive),
      )
      .subscribe((empty) => this.personalFlaggedEmpty.set(empty));

    combineLatest([
      this.foldersListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getTeam(),
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.foldersFilters = new FoldersFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.filesListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getTeam(),
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // short: true - to load FileSummaryDto
        this.filesFilters = new FilesFilters({ short: true, displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.openFileRequest
      .pipe(
        switchMap((file) =>
          this.stitchServiceFactory.getStitchItemById(file.itemType, file.id).pipe(
            take(1),
            catchError(() => EMPTY),
          ),
        ),
        takeUntil(this.alive),
      )
      .subscribe((fullFile) => {
        this.stateService.setMainView(fullFile);
      });

    this.splitViewService
      .getMinimized('filesList')
      .pipe(takeUntil(this.alive))
      .subscribe((minimized: boolean) => {
        this.minimized = minimized;
      });

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
        // Unconditional, not just on the non-'tabs' branch above: a container recreated by
        // stchSpaceRerender (e.g. on a space switch) re-inits `selectedTab` to its field default, but
        // the app-state service instance survives the switch and keeps whatever tab was last published
        // — republishing here on every emission, including while folder-mapped to 'tabs', overwrites
        // that stale value as soon as this container exists instead of only once the folder next
        // changes to a concrete one.
        this.stateService.setSelectedTab(this.selectedTab);
      });

    combineLatest([this.searchService.getState(), this.stateService.getVirtualFolder()])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, folder]) => {
        this.sidebarFilter = folder;
        this.containerName = state[Application.files]?.containerName ?? null;
        this.quickFile = File.fromAdvancedState(state, folder);
        this.quickFolder = Folder.fromAdvancedState(state, folder);
      });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  protected handleSelectedTabChange(tab: string): void {
    if (!isKnownTab<Exclude<ListState, 'tabs'>>(tab, this.tabs)) {
      return;
    }
    this.selectedTab = tab;
    this.stateService.setSelectedTab(tab);
  }

  handleNewFile(file = new File()) {
    this.stateService.setMainView(file);
  }

  handleNewFolder(folder = new Folder()) {
    this.stateService.setMainView(folder);
  }

  handleDoubleClickFile(file: File) {
    if (hasFileLink(file)) {
      openFileLink(file.link);
      return;
    }
    this.stitchServiceFactory
      .getStitchItemById(file.itemType, file.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((fullFile) => {
        this.modalService.openFullForm(fullFile, this.injector);
      });
  }

  handleDoubleClickFolder(folder: Folder) {
    this.modalService.openFullForm(folder, this.injector);
  }

  handleClickFile(file: File) {
    this.openFileRequest.next(file);
  }

  handleClickFolder(folder: Folder) {
    this.stateService.setMainView(folder);
  }

  doDebug() {
    switch (this.debug) {
      case 'score':
        this.debug = null;
        break;
      default:
        this.debug = 'score';
    }
  }
}
